import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { changePageTitle } from 'src/utils/hooks/usePageTitle';
import { useTHelper } from 'src/utils/i18n';
import { CheckoutConfirmationFundraising } from 'src/pages/Checkout/components/Fundraising';
import { useAnyRequestState } from 'src/utils/hooks/useAnyRequestState';
import { apiGetCheckoutContributionConfirmationData } from 'src/api/checkout';
import {
  CheckoutConfirmationSavePreferencesRequestType,
  CheckoutContributionConfirmationResponseType,
} from 'src/models/Checkout/Confirmation';
import { CheckoutConfirmationPublicNotice } from 'src/pages/Checkout/ContributionConfirmation/components/PublicNotice';
import { useStateForm } from 'src/utils/stateForm';
import { CheckoutConfirmationNotificationPreferences } from 'src/pages/Checkout/ContributionConfirmation/components/NotificationPreferences';
import { CheckoutConfirmationSpecialInstructions } from 'src/pages/Checkout/ContributionConfirmation/components/SpecialInstructions';
import { CheckoutConfirmationThankYou } from 'src/pages/Checkout/components/ThankYou';
import { CheckoutConfirmationCheckInformation } from 'src/pages/Checkout/components/CheckInformation';
import { CheckoutConfirmationRegistrationLines } from 'src/pages/Checkout/components/RegistrationLines';
import { CheckoutConfirmationPaymentsSummary } from 'src/pages/Checkout/components/PaymentsSummary';
import { CheckoutConfirmationPaymentMethodSubsection } from 'src/pages/Checkout/ContributionConfirmation/components/PaymentMethodSubsection';
import { CheckoutContributionAdditionalInfo } from 'src/pages/Checkout/ContributionConfirmation/components/AdditionalInfo';
import { CheckoutConfirmationLayout } from 'src/pages/Checkout/components/common/Layout';
import { CheckoutConfirmationItemsSummary } from 'src/pages/Checkout/components/common/ItemsSummary';
import { CheckoutConfirmationBidExplainerText } from 'src/pages/Checkout/components/BidExplainerText';
import { CheckoutConfirmationCheckInInstructions } from 'src/pages/Checkout/components/CheckInInstructions';
import { QrCodeCheckIn } from 'src/pages/Checkout/components/QrCodeCheckIn';

export type CheckoutContributionConfirmationFormValues = CheckoutConfirmationSavePreferencesRequestType;

export const CheckoutContributionConfirmationPage: FC = () => {
  const tHelper = useTHelper('checkout.confirmation');

  const formProps = useStateForm<CheckoutContributionConfirmationFormValues>();

  const { id } = useParams<{ id?: string }>();

  const [model, { loading }] = useAnyRequestState<CheckoutContributionConfirmationResponseType>(
    'getCheckoutContributionConfirmationData',
    {
      data: id,
      apiFunction: apiGetCheckoutContributionConfirmationData,
      onSuccess: (response) => {
        changePageTitle(response.Title);

        formProps.reset({
          ContributionGuid: response.ContributionGuid,
          SpecialInstructions: response.SpecialInstructionsSubsection.Text,
          ReceiveEmailCampaignUpdates: response.NotificationPreferencesSubsection.ReceiveEmailCampaignUpdates,
        });
      },
    }
  );

  return (
    <CheckoutConfirmationLayout model={model} loading={loading} formProps={formProps}>
      {model && (
        <>
          {model.FundraisingPageSubsection.IsVisible && (
            <CheckoutConfirmationFundraising model={model.FundraisingPageSubsection} />
          )}
          {model.PublicNoticeSubsection.IsVisible && (
            <CheckoutConfirmationPublicNotice model={model.PublicNoticeSubsection} />
          )}
          {model.NotificationPreferencesSubsection.IsVisible && (
            <CheckoutConfirmationNotificationPreferences
              model={model.NotificationPreferencesSubsection}
              formProps={formProps}
            />
          )}
          {model.SpecialInstructionsSubsection.IsVisible && (
            <CheckoutConfirmationSpecialInstructions
              model={model.SpecialInstructionsSubsection}
              formProps={formProps}
            />
          )}
          {model.ThankYouSubsection.IsVisible && <CheckoutConfirmationThankYou model={model.ThankYouSubsection} />}
          {model.CheckInInstructionsSubsection.IsVisible && (
            <CheckoutConfirmationCheckInInstructions model={model.CheckInInstructionsSubsection} />
          )}

          {model.QrCodeCheckInSubsection.IsVisible && <QrCodeCheckIn model={model.QrCodeCheckInSubsection} />}

          {model.CheckInformationSubsection.IsVisible && (
            <CheckoutConfirmationCheckInformation model={model.CheckInformationSubsection} />
          )}
          {model.RegistrationLinesSubsection.IsVisible && (
            <CheckoutConfirmationRegistrationLines model={model.RegistrationLinesSubsection} />
          )}

          {model.ShowSummarySection && (
            <>
              <CheckoutConfirmationItemsSummary model={model} />

              {model.ShowPaymentsSummarySection && (
                <>
                  <h2 className="section-header">{model.PaymentSummaryTitle}</h2>
                  {model.ChargedNowPaymentSubsection.IsVisible &&
                    (model.BidPaymentSubsection.IsVisible || model.PledgePaymentSubsection.IsVisible) &&
                    (model.AuctionGiftPaymentSubsection.IsVisible || model.AuctionGiftPaymentSubsection.IsVisible) && (
                      <div className="subsection-header">{tHelper('chargedNow')}</div>
                    )}
                  {model.ChargedNowPaymentSubsection.IsVisible && (
                    <CheckoutConfirmationPaymentsSummary model={model.ChargedNowPaymentSubsection} />
                  )}
                  {model.ShowChargedNowPaymentMethodSubsection && model.ChargedNowPaymentMethodModel && (
                    <CheckoutConfirmationPaymentMethodSubsection model={model.ChargedNowPaymentMethodModel} />
                  )}
                  {(model.BidPaymentSubsection.IsVisible || model.PledgePaymentSubsection.IsVisible) && (
                    <div className="subsection-header">{tHelper('chargedLater')}</div>
                  )}
                  {model.BidPaymentSubsection.IsVisible && (
                    <>
                      <CheckoutConfirmationBidExplainerText />
                      <CheckoutConfirmationPaymentsSummary model={model.BidPaymentSubsection} />
                    </>
                  )}
                  {model.AuctionGiftPaymentSubsection.IsVisible && (
                    <CheckoutConfirmationPaymentsSummary model={model.AuctionGiftPaymentSubsection} />
                  )}
                  {model.PledgePaymentSubsection.IsVisible && (
                    <CheckoutConfirmationPaymentsSummary model={model.PledgePaymentSubsection} />
                  )}
                  {model.ShowChargedLaterPaymentMethodSubsection && (
                    <CheckoutConfirmationPaymentMethodSubsection model={model.ChargedLaterPaymentMethodModel} />
                  )}
                </>
              )}
            </>
          )}
          {model.AdditionalInfoSubsection.IsVisible && (
            <CheckoutContributionAdditionalInfo model={model.AdditionalInfoSubsection} />
          )}
        </>
      )}
    </CheckoutConfirmationLayout>
  );
};
